import React, { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Input, Slider, Radio, Switch, InputNumber, Upload, Button, message } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import InputColor from "react-input-color";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// ✅ Keep base flexible; you can override globally later
const ASSET_BASE =
  window.NETSHINE_ASSETS_BASE_URL ||
  "https://netshine-assets.blr1.cdn.digitaloceanspaces.com";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

function hexToRgba(hex = "#000000", alpha = 1) {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${alpha})`;
}

function resolveAssetUrl(keyOrUrl) {
  if (!keyOrUrl) return "";
  if (/^https?:\/\//i.test(keyOrUrl)) return keyOrUrl;
  return `${ASSET_BASE}/${keyOrUrl}`.replace(/([^:]\/)\/+/g, "$1");
}

export default function BrandingAndSectionsEditor({ config, onChange }) {
  const branding = config?.branding || {};
  const hero = branding?.hero || {};
  const logo = branding?.logo || {};

  const heroBgType = hero?.bgType || hero?.backgroundType || "image";

  const heroImageKey = config?.hero_image || ""; // ✅ stored from upload route / DB column

  const [uploadingHero, setUploadingHero] = useState(false);

  // Never mutate original
  const updateConfig = (mutateFn) => {
    const next = structuredClone(config);
    mutateFn(next);
    onChange(next);
  };

  const updateTheme = (key, value) => {
    updateConfig((next) => {
      next.theme = next.theme || {};
      next.theme[key] = value;
    });
  };

  const updateHero = (key, value) => {
    updateConfig((next) => {
      next.branding = next.branding || {};
      next.branding.hero = next.branding.hero || {};
      next.branding.hero[key] = value;

      // ✅ keep backward compatibility if some files still read backgroundType
      if (key === "bgType") next.branding.hero.backgroundType = value;
    });
  };

  const updateLogo = (key, value) => {
    updateConfig((next) => {
      next.branding = next.branding || {};
      next.branding.logo = next.branding.logo || {};
      next.branding.logo[key] = value;
    });
  };

  // ✅ Upload hero image -> updates DB column reservation_widget_configs.hero_image via your route
// ✅ Upload hero image -> sends ONLY `image` (matches multer.single("image"))
const heroUploadRequest = async ({ file, onSuccess, onError }) => {
  try {
    setUploadingHero(true);

    // AntD Upload sometimes passes RcFile; safest:
    const realFile = file?.originFileObj || file;
    if (!realFile) throw new Error("No file found to upload");

    const form = new FormData();
    form.append("image", realFile); // ✅ MUST match .single("image")

    const res = await axios.post(
      `${apiUrl}/pos/reservations/upload-hero-image`,
      form,
      {
        headers: {
          ...authHeaders().headers,
          // ❌ DO NOT set Content-Type here (axios will set boundary)
        },
      }
    );

    // Your backend returns: { success:true, fileKey, url }
    const fileKey = res?.data?.fileKey || res?.data?.key || "";
    const url = res?.data?.url || "";

    if (!fileKey && !url) {
      throw new Error("Upload succeeded but no fileKey/url returned");
    }

    updateConfig((next) => {
      // keep DB key in root field (as you want: reservation_widget_configs.hero_image)
      next.hero_image = fileKey || next.hero_image;

      // keep hero in image mode
      next.branding = next.branding || {};
      next.branding.hero = next.branding.hero || {};
      next.branding.hero.bgType = "image";

      // ✅ For immediate preview, use CDN URL
      // Priority rule: hero_image (DB) > mediaUrl (manual)
      if (url) next.branding.hero.mediaUrl = url;
    });

    message.success("Hero image uploaded");
    onSuccess?.(res.data);
  } catch (err) {
    console.error("Hero upload failed:", err);
    message.error(err?.response?.data?.error || "Hero upload failed");
    onError?.(err);
  } finally {
    setUploadingHero(false);
  }
};


  const deleteHeroImage = async () => {
    try {
      setUploadingHero(true);

      // 🔁 IMPORTANT:
      // If your delete route differs, change it here only.
      await axios.delete(`${apiUrl}/pos/reservations/hero-image`, authHeaders());

      updateConfig((next) => {
        next.hero_image = "";
      });

      message.success("Hero image removed");
    } catch (err) {
      console.error(err);
      message.error("Could not delete hero image");
    } finally {
      setUploadingHero(false);
    }
  };

  return (
    <div>
      {/* ========================= THEME ========================== */}
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="mb-2">Theme</h5>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Used across buttons, accents, highlights and the nav glow.
          </div>

          <Row className="g-3 mt-2">
            <Col md={6}>
              <label className="form-label">Primary Theme Color</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={config?.theme?.primary || "#9df03d"}
                  onChange={(c) => updateTheme("primary", c.hex)}
                />
                <Input
                  style={{ width: 160 }}
                  value={config?.theme?.primary || "#9df03d"}
                  onChange={(e) => updateTheme("primary", e.target.value)}
                />
              </div>
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Tip: This will tint nav glow + primary CTA gradients in preview.
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Button Text Color (on primary)</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={config?.theme?.onPrimary || "#05070a"}
                  onChange={(c) => updateTheme("onPrimary", c.hex)}
                />
                <Input
                  style={{ width: 160 }}
                  value={config?.theme?.onPrimary || "#05070a"}
                  onChange={(e) => updateTheme("onPrimary", e.target.value)}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* ========================= LOGO ========================== */}
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="mb-2">Logo</h5>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Controls the top-left logo on the dedicated page (and preview).
          </div>

          <div className="d-flex align-items-center gap-2 mt-3">
            <Switch checked={logo.showLogo ?? true} onChange={(v) => updateLogo("showLogo", v)} />
            <span style={{ fontWeight: 600 }}>Show Logo (top-left)</span>
          </div>

          <Row className="g-3 mt-2">
            <Col md={12}>
              <label className="form-label">Logo URL</label>
              <Input
                value={logo.logoUrl || ""}
                onChange={(e) => updateLogo("logoUrl", e.target.value)}
                placeholder="https://.../logo-dark.png"
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Leave blank to use default logo.
              </div>
            </Col>

            <Col md={12}>
              <label className="form-label">Light Logo URL (optional)</label>
              <Input
                value={logo.lightLogoUrl || ""}
                onChange={(e) => updateLogo("lightLogoUrl", e.target.value)}
                placeholder="https://.../logo-light.png"
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Used on dark/transparent nav. If empty, Logo URL is used.
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Logo Width (px) (80–400)</label>
              <InputNumber
                min={80}
                max={400}
                style={{ width: "100%" }}
                value={logo.width ?? 180}
                onChange={(v) => updateLogo("width", v)}
              />
            </Col>

            <Col md={6}>
              <label className="form-label">Logo Height (px) (40–260)</label>
              <InputNumber
                min={40}
                max={260}
                style={{ width: "100%" }}
                value={logo.height ?? 70}
                onChange={(v) => updateLogo("height", v)}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* ========================= HERO ========================== */}
      <div className="card">
        <div className="card-body">
          <h4 className="mb-3">Hero</h4>

          <Row className="g-3">
            <Col md={12}>
              <label className="form-label">Title</label>
              <Input value={hero.headline || ""} onChange={(e) => updateHero("headline", e.target.value)} />
            </Col>

            <Col md={6}>
              <label className="form-label">Title Color</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={hero.headlineColor || "#ffffff"}
                  onChange={(c) => updateHero("headlineColor", c.hex)}
                />
                <Input
                  style={{ width: 160 }}
                  value={hero.headlineColor || "#ffffff"}
                  onChange={(e) => updateHero("headlineColor", e.target.value)}
                />
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Title Font Size ({hero.headlineFontSize || 48}px)</label>
              <Slider min={24} max={72} value={hero.headlineFontSize || 48} onChange={(v) => updateHero("headlineFontSize", v)} />
            </Col>

            <Col md={12}>
              <label className="form-label">Subtitle</label>
              <Input value={hero.subline || ""} onChange={(e) => updateHero("subline", e.target.value)} />
            </Col>

            <Col md={6}>
              <label className="form-label">Subtitle Color</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={hero.sublineColor || "#d1d5db"}
                  onChange={(c) => updateHero("sublineColor", c.hex)}
                />
                <Input
                  style={{ width: 160 }}
                  value={hero.sublineColor || "#d1d5db"}
                  onChange={(e) => updateHero("sublineColor", e.target.value)}
                />
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Subtitle Font Size ({hero.sublineFontSize || 16}px)</label>
              <Slider min={12} max={28} value={hero.sublineFontSize || 16} onChange={(v) => updateHero("sublineFontSize", v)} />
            </Col>

            <Col md={6}>
              <label className="form-label">Hero Height Desktop (px)</label>
              <InputNumber
                min={320}
                max={860}
                style={{ width: "100%" }}
                value={hero.heightDesktop ?? 520}
                onChange={(v) => updateHero("heightDesktop", v ?? 520)}
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Lets you reduce hero height without breaking layout.
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Hero Height Mobile (px)</label>
              <InputNumber
                min={420}
                max={980}
                style={{ width: "100%" }}
                value={hero.heightMobile ?? 640}
                onChange={(v) => updateHero("heightMobile", v ?? 640)}
              />
            </Col>
          </Row>

          <hr className="my-4" />

          <h5 className="mb-2">Hero Background</h5>

          <Radio.Group
            value={heroBgType}
            onChange={(e) => updateHero("bgType", e.target.value)}
            className="mb-3"
          >
            <Radio value="color">Color</Radio>
            <Radio value="image">Image</Radio>
            <Radio value="video">Video</Radio>
          </Radio.Group>

          {heroBgType === "color" && (
            <div className="mb-3">
              <label className="form-label">Background Color</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={hero.bgColor || "#05070a"}
                  onChange={(c) => updateHero("bgColor", c.hex)}
                />
                <Input
                  style={{ width: 200 }}
                  value={hero.bgColor || "#05070a"}
                  onChange={(e) => updateHero("bgColor", e.target.value)}
                />
              </div>
            </div>
          )}

          {(heroBgType === "image" || heroBgType === "video") && (
            <>
              {/* ✅ Upload controls (this is what you’re missing) */}
              {heroBgType === "image" && (
                <div className="mb-3">
                  <label className="form-label">Upload Hero Image (recommended)</label>

                  <div className="d-flex align-items-center gap-10 flex-wrap" style={{ gap: 10 }}>
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      customRequest={heroUploadRequest}
                      disabled={uploadingHero}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadingHero}>
                        Upload Image
                      </Button>
                    </Upload>

                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!heroImageKey || uploadingHero}
                      onClick={deleteHeroImage}
                    >
                      Remove Uploaded
                    </Button>

                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Uploaded image is stored as <code>reservation_widget_configs.hero_image</code> and has priority.
                    </div>
                  </div>

                  {heroImageKey ? (
                    <div
                      className="mt-3"
                      style={{
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.08)",
                        background: "#fff"
                      }}
                    >
                      <img
                        src={resolveAssetUrl(heroImageKey)}
                        alt="Hero preview"
                        style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
                      />
                      <div style={{ padding: 10, fontSize: 12, color: "#6b7280" }}>
                        Current uploaded key: <code>{heroImageKey}</code>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">
                  {heroBgType === "image" ? "Image URL (fallback)" : "Video URL"}
                </label>
                <Input
                  value={hero.mediaUrl || ""}
                  onChange={(e) => updateHero("mediaUrl", e.target.value)}
                  placeholder="https://..."
                />
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  If uploaded hero image is blank, preview/site uses this URL.
                </div>
              </div>
            </>
          )}

          <hr className="my-4" />

          <h5 className="mb-2">Overlay</h5>

          <Row className="g-3">
            <Col md={6}>
              <label className="form-label">Overlay Color</label>
              <div className="d-flex align-items-center gap-3">
                <InputColor
                  initialValue={hero.overlayColor || "#000000"}
                  onChange={(c) => updateHero("overlayColor", c.hex)}
                />
                <Input
                  style={{ width: 200 }}
                  value={hero.overlayColor || "#000000"}
                  onChange={(e) => updateHero("overlayColor", e.target.value)}
                />
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Overlay Opacity ({hero.overlayOpacity ?? 0.45})</label>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={hero.overlayOpacity ?? 0.45}
                onChange={(v) => updateHero("overlayOpacity", v)}
              />
              <div className="text-muted" style={{ fontSize: 12 }}>
                0 = none, 1 = fully dark. (Uses {hexToRgba(hero.overlayColor, hero.overlayOpacity ?? 0.45)})
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
